/**
 * Admin section for runtime AI configuration.
 *
 * Three parts:
 *  1. Provider collection: add / edit / delete / test any number of providers
 *     (OpenAI-compatible or Amazon Bedrock), each with its own name, connection,
 *     default model and enabled flag.
 *  2. Global switch: the master "AI aktiviert" toggle + the default provider that
 *     features inherit when they pin none.
 *  3. Per-feature routing: each AI feature can pick its own provider + model +
 *     thinking mode, inheriting the default provider/model when left on "Standard".
 *
 * Security properties (matching server contract):
 * - api_key is WRITE-ONLY: the server never returns it; we only ever send it.
 * - When has_api_key is true and the key field is left blank, the existing key is
 *   kept (api_key omitted from the payload).
 * - Enabling AI informs the admin that task data is sent to the provider.
 */
import { useEffect, useMemo, useState } from "preact/hooks";
import { Bot, CheckCircle2, Pencil, Plus, RefreshCw, Trash2, XCircle } from "lucide-preact";
import {
  AI_FEATURE_KEYS,
  AI_FEATURE_LABELS,
  type AiFeatureConfigDto,
  type AiFeatureThinkingMode,
  type AiProvider,
  type AiProviderDto,
  type AiThinkingMode,
} from "@juuplan/shared";
import { toastError, toastSuccess } from "../lib/toast";
import { confirmDialog } from "../lib/confirm";
import {
  getAiProviders,
  createAiProvider,
  updateAiProvider,
  deleteAiProvider,
  testAiProvider,
  getAiProviderModels,
  updateAiSettings,
  buildAiProviderPayload,
  getAiFeatures,
  updateAiFeatures,
} from "../lib/api";

const INPUT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";
const SELECT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";
const LABEL_CLS = "block text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-1";
const BTN_PRIMARY =
  "rounded bg-[color:var(--color-primary)] px-4 py-2 text-sm font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";
const BTN_OUTLINE =
  "rounded border border-[color:var(--color-outline-variant)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const THINKING_DEFAULT_LABELS: Record<AiThinkingMode, string> = {
  off: "Aus (empfohlen)",
  on: "An",
  auto: "Auto",
};
const THINKING_FEATURE_LABELS: Record<AiFeatureThinkingMode, string> = {
  inherit: "Standard",
  off: "Aus",
  on: "An",
  auto: "Auto",
};

/** Local editor draft for a provider (create or edit). */
interface ProviderDraft {
  /** null ⇒ creating a new provider; a number ⇒ editing that id. */
  id: number | null;
  name: string;
  provider: AiProvider;
  base_url: string;
  region: string;
  model: string;
  /** Curated allowlist of models offered in the dropdowns (empty ⇒ no restriction). */
  allowed_models: string[];
  enabled: boolean;
  has_api_key: boolean;
}

function blankDraft(): ProviderDraft {
  return {
    id: null,
    name: "",
    provider: "openai_compatible",
    base_url: "",
    region: "",
    model: "",
    allowed_models: [],
    enabled: false,
    has_api_key: false,
  };
}

export function AiSettingsSection(): preact.JSX.Element {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Provider collection + global state
  const [providers, setProviders] = useState<AiProviderDto[]>([]);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [defaultProviderId, setDefaultProviderId] = useState<number | null>(null);
  const [savingGlobal, setSavingGlobal] = useState(false);

  // Model lists keyed by provider id (best-effort discovery).
  const [modelsByProvider, setModelsByProvider] = useState<Record<number, string[]>>({});

  // Provider editor
  const [draft, setDraft] = useState<ProviderDraft | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [changingKey, setChangingKey] = useState(false);
  const [manualModel, setManualModel] = useState("");
  const [savingProvider, setSavingProvider] = useState(false);
  const [regionError, setRegionError] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, { ok: boolean }>>({});

  // Per-feature config
  const [thinkingDefault, setThinkingDefault] = useState<AiThinkingMode>("off");
  const [features, setFeatures] = useState<AiFeatureConfigDto[]>([]);
  const [savingFeatures, setSavingFeatures] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load(): Promise<void> {
    setLoading(true);
    setLoadError(null);
    try {
      const [prov, feat] = await Promise.all([getAiProviders(), getAiFeatures()]);
      setProviders(prov.providers);
      setGlobalEnabled(prov.enabled);
      setDefaultProviderId(prov.default_provider_id);
      setThinkingDefault(feat.thinking_default);
      setFeatures(feat.features);
      // Best-effort model discovery for every provider — never blocks the form.
      void refreshAllModels(prov.providers);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }

  async function refreshAllModels(list: AiProviderDto[]): Promise<void> {
    const entries = await Promise.all(
      list.map(async (p): Promise<[number, string[]]> => {
        try {
          const res = await getAiProviderModels(p.id);
          return [p.id, res.models];
        } catch {
          // Absichtlich stumm wie in refreshOneModels: Modell-Discovery ist
          // best-effort (Anbieter offline / Schluessel fehlt). Bei mehreren
          // Anbietern gaebe es sonst einen Toast pro Anbieter.
          return [p.id, []];
        }
      })
    );
    setModelsByProvider(Object.fromEntries(entries));
  }

  async function refreshOneModels(id: number): Promise<void> {
    try {
      const res = await getAiProviderModels(id);
      setModelsByProvider((prev) => ({ ...prev, [id]: res.models }));
    } catch {
      // Discovery is best-effort (provider may be down / key unset).
    }
  }

  // ---- Provider editor -----------------------------------------------------

  function startCreate(): void {
    setDraft(blankDraft());
    setApiKey("");
    setChangingKey(false);
    setManualModel("");
    setRegionError(null);
  }

  function startEdit(p: AiProviderDto): void {
    setDraft({
      id: p.id,
      name: p.name,
      provider: p.provider,
      base_url: p.base_url ?? "",
      region: p.region ?? "",
      model: p.model ?? "",
      allowed_models: p.allowed_models ?? [],
      enabled: p.enabled,
      has_api_key: p.has_api_key,
    });
    setApiKey("");
    setChangingKey(false);
    setManualModel("");
    setRegionError(null);
  }

  function cancelEdit(): void {
    setDraft(null);
    setApiKey("");
    setChangingKey(false);
    setManualModel("");
    setRegionError(null);
  }

  function patchDraft(patch: Partial<ProviderDraft>): void {
    setDraft((d) => (d ? { ...d, ...patch } : d));
  }

  /** Models to show in the editor checklist: discovered ∪ already-curated. */
  function editorChecklistModels(d: ProviderDraft): string[] {
    const set = new Set<string>(d.id !== null ? (modelsByProvider[d.id] ?? []) : []);
    for (const m of d.allowed_models) set.add(m);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  function toggleAllowed(model: string): void {
    setDraft((d) => {
      if (!d) return d;
      const has = d.allowed_models.includes(model);
      return {
        ...d,
        allowed_models: has
          ? d.allowed_models.filter((m) => m !== model)
          : [...d.allowed_models, model],
      };
    });
  }

  function addManualModel(): void {
    const m = manualModel.trim();
    if (!m) return;
    setDraft((d) => (d && !d.allowed_models.includes(m) ? { ...d, allowed_models: [...d.allowed_models, m] } : d));
    setManualModel("");
  }

  function handleProviderTypeChange(next: AiProvider): void {
    setRegionError(null);
    patchDraft({ provider: next });
    if (next === "bedrock" && draft && draft.region.trim().length === 0) {
      patchDraft({ region: "eu-central-1" });
    }
  }

  async function handleSaveProvider(): Promise<void> {
    if (!draft) return;
    if (draft.name.trim().length === 0) {
      toastError("Bitte einen Namen für den Anbieter vergeben.");
      return;
    }
    if (draft.provider === "bedrock") {
      const trimmedRegion = draft.region.trim();
      if (trimmedRegion.length === 0 || !trimmedRegion.startsWith("eu-")) {
        setRegionError(
          "AWS-Region ist erforderlich und muss eine EU-Region sein (z. B. eu-central-1)."
        );
        return;
      }
      setRegionError(null);
    }
    setSavingProvider(true);
    try {
      const body = buildAiProviderPayload(
        draft.provider,
        draft.name.trim(),
        draft.base_url,
        draft.region,
        draft.model,
        draft.allowed_models,
        apiKey,
        draft.enabled
      );
      const saved =
        draft.id === null ? await createAiProvider(body) : await updateAiProvider(draft.id, body);
      toastSuccess("Anbieter gespeichert.");
      setDraft(null);
      setApiKey("");
      setChangingKey(false);
      // Reload the list + this provider's models.
      await load();
      void refreshOneModels(saved.id);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingProvider(false);
    }
  }

  async function handleDeleteProvider(p: AiProviderDto): Promise<void> {
    const ok = await confirmDialog({
      title: "Anbieter löschen",
      message: `Anbieter „${p.name}" wirklich löschen?`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteAiProvider(p.id);
      toastSuccess("Anbieter gelöscht.");
      await load();
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    }
  }

  async function handleTestProvider(id: number): Promise<void> {
    setTestingId(id);
    setTestResults((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const result = await testAiProvider(id);
      setTestResults((prev) => ({ ...prev, [id]: { ok: result.ok } }));
    } catch (err) {
      setTestResults((prev) => ({ ...prev, [id]: { ok: false } }));
      toastError(err instanceof Error ? err.message : "Verbindungstest fehlgeschlagen");
    } finally {
      setTestingId(null);
    }
  }

  // ---- Global switch -------------------------------------------------------

  async function handleSaveGlobal(): Promise<void> {
    if (globalEnabled && defaultProviderId === null) {
      toastError("Bitte zuerst einen Standard-Anbieter wählen, um die KI zu aktivieren.");
      return;
    }
    setSavingGlobal(true);
    try {
      const dto = await updateAiSettings({
        enabled: globalEnabled,
        default_provider_id: defaultProviderId,
      });
      setGlobalEnabled(dto.enabled);
      setDefaultProviderId(dto.default_provider_id);
      toastSuccess("Globale KI-Einstellungen gespeichert.");
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingGlobal(false);
    }
  }

  // ---- Per-feature ---------------------------------------------------------

  function updateFeature(key: string, patch: Partial<AiFeatureConfigDto>): void {
    setFeatures((prev) => prev.map((f) => (f.feature_key === key ? { ...f, ...patch } : f)));
  }

  async function handleSaveFeatures(): Promise<void> {
    setSavingFeatures(true);
    try {
      const res = await updateAiFeatures({ thinking_default: thinkingDefault, features });
      setThinkingDefault(res.thinking_default);
      setFeatures(res.features);
      toastSuccess("Feature-Einstellungen gespeichert.");
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingFeatures(false);
    }
  }

  const providerName = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of providers) m.set(p.id, p.name);
    return m;
  }, [providers]);

  /**
   * Model options for a feature: the curated allowlist of its resolved provider
   * (empty ⇒ no restriction, fall back to discovered), plus any already-set value.
   */
  function featureModelOptions(f: AiFeatureConfigDto): string[] {
    const pid = f.provider_id ?? defaultProviderId;
    const prov = pid !== null ? providers.find((p) => p.id === pid) : undefined;
    const base =
      prov && prov.allowed_models.length > 0
        ? prov.allowed_models
        : pid !== null
          ? (modelsByProvider[pid] ?? [])
          : [];
    const set = new Set<string>(base);
    if (f.model) set.add(f.model);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  if (loading) {
    return (
      <p class="text-sm text-[color:var(--color-on-surface-variant)]">Lädt KI-Einstellungen…</p>
    );
  }
  if (loadError) {
    return <p class="text-sm text-[color:var(--color-error)]">Fehler: {loadError}</p>;
  }

  const keyIsSet = draft?.has_api_key ?? false;
  const showKeyInput = changingKey || !keyIsSet;

  return (
    <section class="space-y-10 max-w-3xl">
      {/* ---- Provider collection ---- */}
      <div class="space-y-4">
        <div class="flex items-center justify-between gap-4">
          <div>
            <h3 class="text-base font-semibold text-[color:var(--color-on-surface)]">Anbieter</h3>
            <p class="text-xs text-[color:var(--color-on-surface-variant)] mt-1">
              Beliebig viele KI-Anbieter konfigurieren. Ein deaktivierter Anbieter ist nicht
              auswählbar.
            </p>
          </div>
          {draft === null && (
            <button type="button" onClick={startCreate} class={`${BTN_PRIMARY} inline-flex items-center gap-1 shrink-0`}>
              <Plus size={16} aria-hidden="true" /> Anbieter hinzufügen
            </button>
          )}
        </div>

        {providers.length === 0 && draft === null && (
          <p class="text-sm text-[color:var(--color-on-surface-variant)]">
            Noch kein Anbieter konfiguriert.
          </p>
        )}

        {/* Provider rows */}
        <div class="space-y-2">
          {providers.map((p) => {
            const tr = testResults[p.id];
            return (
              <div
                key={p.id}
                class="flex items-center gap-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2"
              >
                <span
                  class={`h-2 w-2 shrink-0 rounded-full ${p.enabled ? "bg-[color:var(--color-primary)]" : "bg-[color:var(--color-outline-variant)]"}`}
                  title={p.enabled ? "Aktiv" : "Deaktiviert"}
                  aria-hidden="true"
                />
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-[color:var(--color-on-surface)] truncate">
                      {p.name}
                    </span>
                    {defaultProviderId === p.id && (
                      <span class="shrink-0 rounded bg-[color:var(--color-primary-container)] px-1.5 py-0.5 text-label font-medium text-[color:var(--color-on-primary-container)]">
                        Standard
                      </span>
                    )}
                  </div>
                  <p class="text-micro text-[color:var(--color-on-surface-variant)] truncate">
                    {p.provider === "bedrock" ? `Bedrock · ${p.region ?? "?"}` : "OpenAI-kompatibel"}
                    {p.model ? ` · ${p.model}` : ""}
                    {!p.has_api_key ? " · kein Schlüssel" : ""}
                  </p>
                </div>
                {tr && (
                  <span
                    class={`inline-flex items-center gap-1 text-xs ${tr.ok ? "text-[color:var(--color-primary)]" : "text-[color:var(--color-error)]"}`}
                  >
                    {tr.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                    {tr.ok ? "OK" : "Fehler"}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => void handleTestProvider(p.id)}
                  disabled={testingId === p.id}
                  class={`${BTN_OUTLINE} shrink-0`}
                >
                  {testingId === p.id ? "Teste…" : "Testen"}
                </button>
                <button
                  type="button"
                  onClick={() => startEdit(p)}
                  aria-label={`Anbieter ${p.name} bearbeiten`}
                  class={`${BTN_OUTLINE} shrink-0`}
                >
                  <Pencil size={14} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeleteProvider(p)}
                  aria-label={`Anbieter ${p.name} löschen`}
                  class={`${BTN_OUTLINE} shrink-0`}
                  // Inline-Style statt zweiter text-*-Utility: BTN_OUTLINE setzt
                  // bereits eine Textfarbe, und bei konkurrierenden Tailwind-
                  // Klassen entscheidet die Stylesheet-Reihenfolge, nicht die
                  // Klassen-Reihenfolge.
                  style={{ color: "var(--color-error)" }}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Provider editor */}
        {draft !== null && (
          <div class="space-y-5 rounded border border-[color:var(--color-outline-variant)] p-4 max-w-lg">
            <h4 class="text-sm font-semibold text-[color:var(--color-on-surface)]">
              {draft.id === null ? "Neuer Anbieter" : "Anbieter bearbeiten"}
            </h4>

            <div>
              <label class={LABEL_CLS} for="ai-name">Name</label>
              <input
                id="ai-name"
                type="text"
                value={draft.name}
                onInput={(e) => patchDraft({ name: (e.target as HTMLInputElement).value })}
                placeholder="z. B. Mittwald Mistral"
                class={INPUT_CLS}
              />
            </div>

            <div>
              <label class={LABEL_CLS} for="ai-provider">Typ</label>
              <select
                id="ai-provider"
                value={draft.provider}
                onChange={(e) => handleProviderTypeChange((e.target as HTMLSelectElement).value as AiProvider)}
                class={SELECT_CLS}
              >
                <option value="openai_compatible">OpenAI-kompatibel</option>
                <option value="bedrock">Amazon Bedrock</option>
              </select>
            </div>

            {draft.provider === "openai_compatible" ? (
              <div>
                <label class={LABEL_CLS} for="ai-base-url">Basis-URL</label>
                <input
                  id="ai-base-url"
                  type="url"
                  value={draft.base_url}
                  onInput={(e) => patchDraft({ base_url: (e.target as HTMLInputElement).value })}
                  placeholder="https://api.openai.com/v1"
                  class={INPUT_CLS}
                  aria-label="Basis-URL des KI-Anbieters"
                />
              </div>
            ) : (
              <div>
                <label class={LABEL_CLS} for="ai-region">AWS-Region * (EU)</label>
                <input
                  id="ai-region"
                  type="text"
                  value={draft.region}
                  onInput={(e) => {
                    patchDraft({ region: (e.target as HTMLInputElement).value });
                    setRegionError(null);
                  }}
                  placeholder="eu-central-1"
                  class={INPUT_CLS}
                  aria-label="AWS-Region für Amazon Bedrock"
                />
                <p class="text-label text-[color:var(--color-on-surface-variant)] mt-1">
                  DSGVO: nur EU-Regionen (z.&nbsp;B. eu-central-1 Frankfurt, eu-west-1 Irland).
                </p>
                {regionError && (
                  <p className="text-xs text-[color:var(--color-error)] mt-1">{regionError}</p>
                )}
              </div>
            )}

            {/* Verfügbare Modelle — curated allowlist. Empty ⇒ no restriction. */}
            <div>
              <div class="flex items-center justify-between gap-2">
                <label class={LABEL_CLS}>Verfügbare Modelle</label>
                {draft.id !== null && (
                  <button
                    type="button"
                    onClick={() => void refreshOneModels(draft.id as number)}
                    title="Modelle vom Anbieter laden"
                    aria-label="Modelle aktualisieren"
                    class="inline-flex items-center gap-1 text-xs text-[color:var(--color-primary)] hover:underline"
                  >
                    <RefreshCw size={14} aria-hidden="true" /> Erkennen
                  </button>
                )}
              </div>
              <p class="text-label text-[color:var(--color-on-surface-variant)] mb-2">
                Haken Sie an, welche Modelle in den Auswahlfeldern (Standard + pro Feature)
                angeboten werden. Leer = keine Einschränkung (alle erkannten Modelle).
              </p>
              {editorChecklistModels(draft).length > 0 ? (
                <div class="max-h-48 overflow-y-auto rounded border border-[color:var(--color-outline-variant)] divide-y divide-[color:var(--color-outline-variant)]">
                  {editorChecklistModels(draft).map((m) => (
                    <label
                      key={m}
                      class="flex items-center gap-2 px-3 py-1.5 text-sm text-[color:var(--color-on-surface)] cursor-pointer hover:bg-[color:var(--color-surface-container-low)]"
                    >
                      <input
                        type="checkbox"
                        checked={draft.allowed_models.includes(m)}
                        onChange={() => toggleAllowed(m)}
                        aria-label={`Modell ${m} verfügbar`}
                      />
                      <span class="font-mono truncate">{m}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p class="text-xs text-[color:var(--color-on-surface-variant)]">
                  {draft.id === null
                    ? "Modell(e) unten manuell hinzufügen (Erkennen ist erst nach dem Speichern möglich)."
                    : "Keine Modelle erkannt — unten manuell hinzufügen oder „Erkennen“."}
                </p>
              )}
              {/* Manual add */}
              <div class="flex items-center gap-2 mt-2">
                <input
                  type="text"
                  value={manualModel}
                  onInput={(e) => setManualModel((e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManualModel();
                    }
                  }}
                  placeholder="Modell manuell hinzufügen…"
                  class={INPUT_CLS}
                  aria-label="Modell manuell hinzufügen"
                />
                <button type="button" onClick={addManualModel} class={`${BTN_OUTLINE} shrink-0`}>
                  Hinzufügen
                </button>
              </div>
            </div>

            <div>
              <label class={LABEL_CLS} for="ai-model">Standard-Modell</label>
              <input
                id="ai-model"
                type="text"
                list="ai-model-list"
                value={draft.model}
                onInput={(e) => patchDraft({ model: (e.target as HTMLInputElement).value })}
                placeholder="gpt-4o-mini"
                class={INPUT_CLS}
                aria-label="Standard-KI-Modell"
              />
              <datalist id="ai-model-list">
                {(draft.allowed_models.length > 0
                  ? draft.allowed_models
                  : draft.id !== null
                    ? (modelsByProvider[draft.id] ?? [])
                    : []
                ).map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
            </div>

            {/* API-Key — write-only UX */}
            <div>
              <label class={LABEL_CLS} for="ai-api-key">API-Schlüssel</label>
              {keyIsSet && !showKeyInput ? (
                <div class="flex items-center gap-2">
                  <span class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2 text-sm text-[color:var(--color-on-surface-variant)] font-mono select-none">
                    •••••• (gesetzt)
                  </span>
                  <button type="button" onClick={() => setChangingKey(true)} class={`${BTN_OUTLINE} shrink-0`}>
                    Ändern
                  </button>
                </div>
              ) : (
                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-2">
                    <input
                      id="ai-api-key"
                      type="password"
                      value={apiKey}
                      onInput={(e) => setApiKey((e.target as HTMLInputElement).value)}
                      placeholder={keyIsSet ? "Neuen Schlüssel eingeben…" : "sk-…"}
                      autocomplete="new-password"
                      class={INPUT_CLS}
                      aria-label="API-Schlüssel"
                    />
                    {keyIsSet && (
                      <button
                        type="button"
                        onClick={() => {
                          setApiKey("");
                          setChangingKey(false);
                        }}
                        class={`${BTN_OUTLINE} shrink-0`}
                      >
                        Abbrechen
                      </button>
                    )}
                  </div>
                  {keyIsSet && (
                    <p class="text-xs text-[color:var(--color-on-surface-variant)]">
                      Leer lassen um den bestehenden Schlüssel zu behalten.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Provider enabled toggle */}
            <div class="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={draft.enabled}
                aria-label="Anbieter aktiviert"
                onClick={() => patchDraft({ enabled: !draft.enabled })}
                class={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
                  draft.enabled
                    ? "bg-[color:var(--color-primary)]"
                    : "bg-[color:var(--color-surface-container-high)]"
                }`}
              >
                <span
                  aria-hidden="true"
                  class={`pointer-events-none inline-block h-4 w-4 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] transition-transform ${
                    draft.enabled ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
              <span class="text-sm text-[color:var(--color-on-surface)]">Anbieter aktiviert</span>
            </div>

            <div class="flex items-center gap-2">
              <button type="button" onClick={() => void handleSaveProvider()} disabled={savingProvider} class={BTN_PRIMARY}>
                {savingProvider ? "Speichert…" : "Speichern"}
              </button>
              <button type="button" onClick={cancelEdit} class={BTN_OUTLINE}>
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---- Global switch ---- */}
      <div class="space-y-4 max-w-lg">
        <div>
          <h3 class="text-base font-semibold text-[color:var(--color-on-surface)]">Globale KI</h3>
          <p class="text-xs text-[color:var(--color-on-surface-variant)] mt-1">
            Master-Schalter und Standard-Anbieter, den Features erben, wenn sie keinen eigenen
            wählen.
          </p>
        </div>

        <div>
          <label class={LABEL_CLS} for="ai-default-provider">Standard-Anbieter</label>
          <select
            id="ai-default-provider"
            value={defaultProviderId ?? ""}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setDefaultProviderId(v === "" ? null : Number(v));
            }}
            class={SELECT_CLS}
          >
            <option value="">— keiner —</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {!p.enabled ? " (deaktiviert)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div class="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={globalEnabled}
            aria-label="KI aktiviert"
            onClick={() => setGlobalEnabled((v) => !v)}
            class={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
              globalEnabled
                ? "bg-[color:var(--color-primary)]"
                : "bg-[color:var(--color-surface-container-high)]"
            }`}
          >
            <span
              aria-hidden="true"
              class={`pointer-events-none inline-block h-4 w-4 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] transition-transform ${
                globalEnabled ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
          <span class="text-sm text-[color:var(--color-on-surface)]">KI aktiviert</span>
        </div>

        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
          Hinweis: Bei aktivierter KI werden Aufgabentitel, Beschreibungen, Status und Kommentare
          an den konfigurierten Anbieter gesendet (Datenweitergabe).
        </div>

        <button type="button" onClick={() => void handleSaveGlobal()} disabled={savingGlobal} class={BTN_PRIMARY}>
          {savingGlobal ? "Speichert…" : "Globale Einstellungen speichern"}
        </button>
      </div>

      {/* ---- Per-feature provider + model + thinking ---- */}
      <div class="space-y-4">
        <div>
          <h3 class="text-base font-semibold text-[color:var(--color-on-surface)]">
            Anbieter &amp; Modelle pro Feature
          </h3>
          <p class="text-xs text-[color:var(--color-on-surface-variant)] mt-1">
            Jedes KI-Feature kann einen eigenen Anbieter, ein eigenes Modell und einen eigenen
            „Thinking"-Modus verwenden. „Standard" erbt den globalen Standard-Anbieter bzw. dessen
            Modell.
          </p>
        </div>

        <div class="max-w-xs">
          <label class={LABEL_CLS} for="ai-thinking-default">Standard „Thinking"-Modus</label>
          <select
            id="ai-thinking-default"
            value={thinkingDefault}
            onChange={(e) => setThinkingDefault((e.target as HTMLSelectElement).value as AiThinkingMode)}
            class={SELECT_CLS}
          >
            {(["off", "on", "auto"] as const).map((m) => (
              <option key={m} value={m}>{THINKING_DEFAULT_LABELS[m]}</option>
            ))}
          </select>
        </div>

        <div class="overflow-x-auto">
          <div class="min-w-[680px] space-y-2">
            <div class="grid grid-cols-[1fr_1fr_1fr_110px] gap-2 text-label font-medium uppercase tracking-wide text-[color:var(--color-on-surface-variant)] px-1">
              <span>Feature</span>
              <span>Anbieter</span>
              <span>Modell</span>
              <span>Thinking</span>
            </div>
            {AI_FEATURE_KEYS.map((key) => {
              const f =
                features.find((x) => x.feature_key === key) ??
                ({ feature_key: key, provider_id: null, model: null, thinking: "inherit" } as AiFeatureConfigDto);
              const defaultName =
                defaultProviderId !== null ? providerName.get(defaultProviderId) : undefined;
              return (
                <div key={key} class="grid grid-cols-[1fr_1fr_1fr_110px] gap-2 items-center">
                  <span class="text-sm text-[color:var(--color-on-surface)] truncate">
                    {AI_FEATURE_LABELS[key]}
                  </span>
                  <select
                    value={f.provider_id ?? ""}
                    onChange={(e) => {
                      const v = (e.target as HTMLSelectElement).value;
                      // Switching provider clears a model that belonged to the old one.
                      updateFeature(key, {
                        provider_id: v === "" ? null : Number(v),
                        model: null,
                      });
                    }}
                    aria-label={`Anbieter für ${AI_FEATURE_LABELS[key]}`}
                    class={SELECT_CLS}
                  >
                    <option value="">Standard{defaultName ? ` (${defaultName})` : ""}</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <select
                    value={f.model ?? ""}
                    onChange={(e) => {
                      const v = (e.target as HTMLSelectElement).value;
                      updateFeature(key, { model: v === "" ? null : v });
                    }}
                    aria-label={`Modell für ${AI_FEATURE_LABELS[key]}`}
                    class={SELECT_CLS}
                  >
                    <option value="">Standard</option>
                    {featureModelOptions(f).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={f.thinking}
                    onChange={(e) =>
                      updateFeature(key, {
                        thinking: (e.target as HTMLSelectElement).value as AiFeatureThinkingMode,
                      })
                    }
                    aria-label={`Thinking für ${AI_FEATURE_LABELS[key]}`}
                    class={SELECT_CLS}
                  >
                    {(["inherit", "off", "on", "auto"] as const).map((m) => (
                      <option key={m} value={m}>{THINKING_FEATURE_LABELS[m]}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>

        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
          Hinweis: „Thinking: An/Auto" bei Reasoning-Modellen (z.&nbsp;B. Qwen3) verbraucht das
          Token-Budget für interne Gedanken. Für die JSON-/Struktur-Features (Plan importieren,
          Neues Projekt) empfiehlt sich „Aus".
        </div>

        <button type="button" onClick={() => void handleSaveFeatures()} disabled={savingFeatures} class={BTN_PRIMARY}>
          {savingFeatures ? "Speichert…" : "Feature-Einstellungen speichern"}
        </button>
      </div>
    </section>
  );
}

/** Icon used by the admin nav entry. Re-exported for Admin.tsx convenience. */
export { Bot as AiSettingsIcon };
